import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    useTheme,
    Chip,
    useMediaQuery,
    Typography
} from "@mui/material";
import { useState, useRef, useMemo } from "react";
import { OrderDetails } from "../../../../../models/DB";
import { LocalShipping, FlashOn, AttachMoney, TrendingUp, HourglassEmpty, Loop, LocalShippingOutlined, CheckCircle } from "@mui/icons-material";
import { OrderStatus, getOrderStatusColor } from "../../../../../models/enums/OrderStatus";
import { OrderRow } from "./OrderRow";
import PortalService from "../../../../../services/supabase/PortalService";
import { PortalStatCard } from "../../../../../components/PortalCard";
import { useStoreConfig } from "../../../../../contexts/StoreConfigContext";
import { TableControlBar } from "../../../../../components/TableControlBar";
import { PortalTable } from "../../../../../components/PortalTable";
import { tablePreferencesStore } from "../../../../../app/stores/TablePreferences";

export const OrdersTable = ({ orders }: { orders: OrderDetails[] }) => {
    const theme = useTheme();
    const { config } = useStoreConfig();
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    // Get preferences from store
    const { 
        orders: ordersPrefs, 
        updateOrdersPreferences 
    } = tablePreferencesStore();
    
    // Use store values with local state for page and search (not persisted)
    const [order, setOrder] = useState<'asc' | 'desc'>(ordersPrefs.sortOrder);
    const [orderBy, setOrderBy] = useState<keyof OrderDetails>(ordersPrefs.sortBy as keyof OrderDetails);
    const [page, setPage] = useState(0); // Session-only
    const [rowsPerPage, setRowsPerPage] = useState<number>(ordersPrefs.rowsPerPage);
    const [dense, setDense] = useState(ordersPrefs.dense);
    const [isLoading, setIsLoading] = useState(false);
    const [deliveryFilter, setDeliveryFilter] = useState<'all' | 'sameday' | 'standard'>(ordersPrefs.deliveryFilter);
    const [statusFilter, setStatusFilter] = useState<'all' | number>('all'); // Session-only status filter
    const [searchQuery, setSearchQuery] = useState(''); // Session-only
    const { getAllOrders } = PortalService();

    // Calculate delivery type statistics
    const deliveryStats = useMemo(() => {
        const samedayOrders = orders?.filter(o => o.deliveryType?.toLowerCase() === 'sameday' || o.deliveryType?.toLowerCase() === 'same day') || [];
        const standardOrders = orders?.filter(o => o.deliveryType?.toLowerCase() === 'standard') || [];

        const sameday = samedayOrders.length;
        const standard = standardOrders.length;
        const other = (orders?.length || 0) - sameday - standard;

        // Calculate revenue by delivery type
        const samedayRevenue = samedayOrders.reduce((sum, o) => sum + (o.totalCost || 0), 0);
        const standardRevenue = standardOrders.reduce((sum, o) => sum + (o.totalCost || 0), 0);

        // Calculate average order value by delivery type
        const samedayAvg = sameday > 0 ? samedayRevenue / sameday : 0;
        const standardAvg = standard > 0 ? standardRevenue / standard : 0;

        // Calculate completion rate by delivery type (status 2 = completed)
        const samedayCompleted = samedayOrders.filter(o => o.status === 2).length;
        const standardCompleted = standardOrders.filter(o => o.status === 2).length;
        const samedayCompletionRate = sameday > 0 ? (samedayCompleted / sameday) * 100 : 0;
        const standardCompletionRate = standard > 0 ? (standardCompleted / standard) * 100 : 0;

        return {
            sameday,
            standard,
            other,
            total: orders?.length || 0,
            samedayRevenue,
            standardRevenue,
            samedayAvg,
            standardAvg,
            samedayCompletionRate,
            standardCompletionRate
        };
    }, [orders]);

    // Calculate status statistics
    const statusStats = useMemo(() => {
        const pending = orders?.filter(o => o.status === OrderStatus.PENDING).length || 0;
        const processing = orders?.filter(o => o.status === OrderStatus.PROCESSING).length || 0;
        const shipped = orders?.filter(o => o.status === OrderStatus.SHIPPED).length || 0;
        const delivered = orders?.filter(o => o.status === OrderStatus.DELIVERED).length || 0;

        return { pending, processing, shipped, delivered };
    }, [orders]);

    // Filter orders by delivery type, status, and search query
    const filteredOrders = useMemo(() => {
        let filtered = orders || [];

        // Apply delivery type filter
        if (deliveryFilter === 'sameday') {
            filtered = filtered.filter(o => o.deliveryType?.toLowerCase() === 'sameday' || o.deliveryType?.toLowerCase() === 'same day');
        } else if (deliveryFilter === 'standard') {
            filtered = filtered.filter(o => o.deliveryType?.toLowerCase() === 'standard');
        }

        // Apply status filter
        if (statusFilter !== 'all') {
            filtered = filtered.filter(o => o.status === statusFilter);
        }

        // Apply search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(o =>
                o.reference?.toLowerCase().includes(query) ||
                o.email?.toLowerCase().includes(query) ||
                o.deliveryType?.toLowerCase().includes(query)
            );
        }

        return filtered;
    }, [orders, deliveryFilter, statusFilter, searchQuery]);

    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - (filteredOrders?.length || 0)) : 0;

    const sortedOrders = filteredOrders?.slice().sort((a, b) => {
        // Handle null/undefined values
        const aValue = a[orderBy];
        const bValue = b[orderBy];

        if (aValue === null && bValue === null) return 0;
        if (aValue === null) return 1;
        if (bValue === null) return -1;
        if (aValue === undefined && bValue === undefined) return 0;
        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;

        let comparison = 0;

        // String columns (case-insensitive)
        if (orderBy === 'reference' || orderBy === 'email' || orderBy === 'deliveryType') {
            comparison = String(aValue).toLowerCase().localeCompare(String(bValue).toLowerCase());
        }
            // Date columns
        else if (orderBy === 'created' || orderBy === 'updated') {
            comparison = new Date(aValue as string).getTime() - new Date(bValue as string).getTime();
        }
        // Number columns
        else if (orderBy === 'status' || orderBy === 'totalCost' || orderBy === 'baseCost' || orderBy === 'vat' || orderBy === 'deliveryFee') {
            comparison = Number(aValue) - Number(bValue);
        }
        // Default: treat as string
        else {
            comparison = String(aValue).localeCompare(String(bValue));
        }

        // Apply sort direction
        const result = order === 'asc' ? comparison : -comparison;

        // If primary sort is equal, apply multi-level secondary sorting
        if (result === 0) {
            // Secondary sort by delivery type (sameday first, then standard)
            if (orderBy !== 'deliveryType') {
                const deliveryOrder = { 'sameday': 1, 'standard': 2, 'nextday': 3 };
                const aDelivery = a.deliveryType?.toLowerCase() || '';
                const bDelivery = b.deliveryType?.toLowerCase() || '';
                const deliveryComparison = (deliveryOrder[aDelivery] || 99) - (deliveryOrder[bDelivery] || 99);

                if (deliveryComparison !== 0) {
                    return deliveryComparison;
                }
            }

            // Tertiary sort: Always sort by created date (latest first) if both primary and secondary are equal
            if (orderBy !== 'created') {
                const aCreated = a.created ? new Date(a.created).getTime() : 0;
                const bCreated = b.created ? new Date(b.created).getTime() : 0;
                return bCreated - aCreated; // Latest first
            }
        }

        return result;
    });

    const handleRequestSort = (_event: React.MouseEvent<unknown>, property: keyof OrderDetails) => {
        const isAsc = orderBy === property && order === 'asc';
        const newOrder = isAsc ? 'desc' : 'asc';
        setOrder(newOrder);
        setOrderBy(property);
        
        // Persist to store
        updateOrdersPreferences({
            sortOrder: newOrder,
            sortBy: property as string
        });
    };

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
        // Scroll to top of table when changing pages
        tableContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        // Save current scroll position before changing
        const currentScrollY = window.scrollY;
        const newRowsPerPage = +event.target.value;

        setRowsPerPage(newRowsPerPage);
        setPage(0);
        
        // Persist to store
        updateOrdersPreferences({ rowsPerPage: newRowsPerPage });

        // Restore scroll position after React re-renders
        requestAnimationFrame(() => {
            window.scrollTo({ top: currentScrollY, behavior: 'instant' });
        });
    };

    const handleRefresh = async () => {
        setIsLoading(true);
        await getAllOrders();
        setIsLoading(false);
    };

    const handleDeliveryFilterChange = (filter: 'all' | 'sameday' | 'standard') => {
        setDeliveryFilter(filter);
        setPage(0); // Reset to first page when filter changes
        
        // Persist to store
        updateOrdersPreferences({ deliveryFilter: filter });
    };

    return (
        <Box
            ref={tableContainerRef}
            sx={{
                width: '100%',
                mb: 4,
            }}
        >
            {/* Delivery Insights - Stats Cards */}
            {deliveryStats.total > 0 && (
                <Box sx={{ mb: 3 }}>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: 'repeat(2, 1fr)',
                                md: 'repeat(3, 1fr)',
                                lg: 'repeat(4, 1fr)'
                            },
                            gap: { xs: 1.5, sm: 2 }
                        }}
                    >
                        {/* Same Day Orders */}
                        <PortalStatCard
                            icon={<FlashOn />}
                            iconBgColor={theme.palette.warning.main}
                            title="Same Day Orders"
                            value={deliveryStats.sameday}
                            subtitle={`${((deliveryStats.sameday / deliveryStats.total) * 100).toFixed(1)}% of total orders`}
                        />

                        {/* Standard Orders */}
                        <PortalStatCard
                            icon={<LocalShipping />}
                            iconBgColor={theme.palette.primary.main}
                            title="Standard Orders"
                            value={deliveryStats.standard}
                            subtitle={`${((deliveryStats.standard / deliveryStats.total) * 100).toFixed(1)}% of total orders`}
                        />

                        {/* Same Day Revenue */}
                        <PortalStatCard
                            icon={<TrendingUp />}
                            iconBgColor={theme.palette.warning.dark}
                            title="Same Day Revenue"
                            value={`${config.currency}${deliveryStats.samedayRevenue.toFixed(0)}`}
                            subtitle={`Avg: ${config.currency}${deliveryStats.samedayAvg.toFixed(0)} • ${deliveryStats.samedayCompletionRate.toFixed(0)}% completed`}
                        />

                        {/* Standard Revenue */}
                        <PortalStatCard
                            icon={<AttachMoney />}
                            iconBgColor={theme.palette.primary.dark}
                            title="Standard Revenue"
                            value={`${config.currency}${deliveryStats.standardRevenue.toFixed(0)}`}
                            subtitle={`Avg: ${config.currency}${deliveryStats.standardAvg.toFixed(0)} • ${deliveryStats.standardCompletionRate.toFixed(0)}% completed`}
                        />
                    </Box>
                </Box>
            )}

            {/* Quick Filter Chips - Mobile visible */}
            {isMobile && (
                <Box sx={{ mb: 2 }}>
                    {/* Delivery Type Filters */}
                    <Box sx={{ mb: 1.5 }}>
                        <Typography
                            variant='caption'
                            sx={{
                                fontFamily: 'primaryFont',
                                fontWeight: 600,
                                color: theme.palette.text.secondary,
                                fontSize: '0.7rem',
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                                mb: 0.75,
                                display: 'block',
                                textAlign: 'left'
                            }}
                        >
                            Delivery
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                                overflowX: 'auto',
                                mt: 0.5,
                                pb: 0.5,
                                '&::-webkit-scrollbar': { height: 3, mt: 0.5 },
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: theme.palette.mode === 'dark'
                                        ? 'rgba(255, 255, 255, 0.2)'
                                        : 'rgba(0, 0, 0, 0.2)',
                                    borderRadius: 2,
                                },
                            }}
                        >
                            <Chip
                                label='All'
                                size='small'
                                variant={deliveryFilter === 'all' ? 'filled' : 'outlined'}
                                onClick={() => handleDeliveryFilterChange('all')}
                                sx={{
                                    fontFamily: 'primaryFont',
                                    fontSize: '0.75rem',
                                    fontWeight: deliveryFilter === 'all' ? 600 : 500,
                                    height: 32,
                                    px: 1.5,
                                    '& .MuiChip-label': { px: 1.5 },
                                    ...(deliveryFilter === 'all' && {
                                        backgroundColor: theme.palette.primary.main,
                                        color: '#fff',
                                    }),
                                }}
                            />
                            <Chip
                                icon={<FlashOn sx={{ fontSize: '0.9rem !important' }} />}
                                label={`Same Day (${deliveryStats.sameday})`}
                                size='small'
                                variant={deliveryFilter === 'sameday' ? 'filled' : 'outlined'}
                                onClick={() => handleDeliveryFilterChange('sameday')}
                                sx={{
                                    fontFamily: 'primaryFont',
                                    fontSize: '0.75rem',
                                    fontWeight: deliveryFilter === 'sameday' ? 600 : 500,
                                    height: 32,
                                    px: 1.5,
                                    '& .MuiChip-label': { px: 1.25 },
                                    ...(deliveryFilter === 'sameday' && {
                                        backgroundColor: theme.palette.warning.main,
                                        color: '#fff',
                                        '& .MuiChip-icon': { color: '#fff' },
                                    }),
                                }}
                            />
                            <Chip
                                icon={<LocalShipping sx={{ fontSize: '0.9rem !important' }} />}
                                label={`Standard (${deliveryStats.standard})`}
                                size='small'
                                variant={deliveryFilter === 'standard' ? 'filled' : 'outlined'}
                                onClick={() => handleDeliveryFilterChange('standard')}
                                sx={{
                                    fontFamily: 'primaryFont',
                                    fontSize: '0.75rem',
                                    fontWeight: deliveryFilter === 'standard' ? 600 : 500,
                                    height: 32,
                                    px: 1.5,
                                    '& .MuiChip-label': { px: 1.25 },
                                    ...(deliveryFilter === 'standard' && {
                                        backgroundColor: theme.palette.info.main,
                                        color: '#fff',
                                        '& .MuiChip-icon': { color: '#fff' },
                                    }),
                                }}
                            />
                        </Box>
                    </Box>

                    {/* Status Filters */}
                    <Box>
                        <Typography
                            variant='caption'
                            sx={{
                                fontFamily: 'primaryFont',
                                fontWeight: 600,
                                color: theme.palette.text.secondary,
                                fontSize: '0.7rem',
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                                mb: 0.75,
                                display: 'block',
                                textAlign: 'left'
                            }}
                        >
                            Status
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                                overflowX: 'auto',
                                mt: 0.5,
                                pb: 0.5,
                                '&::-webkit-scrollbar': { height: 3, mt: 0.5 },
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: theme.palette.mode === 'dark'
                                        ? 'rgba(255, 255, 255, 0.2)'
                                        : 'rgba(0, 0, 0, 0.2)',
                                    borderRadius: 2,
                                },
                            }}
                        >
                            <Chip
                                label='All'
                                size='small'
                                variant={statusFilter === 'all' ? 'filled' : 'outlined'}
                                onClick={() => setStatusFilter('all')}
                                sx={{
                                    fontFamily: 'primaryFont',
                                    fontSize: '0.75rem',
                                    fontWeight: statusFilter === 'all' ? 600 : 500,
                                    height: 32,
                                    px: 1.5,
                                    '& .MuiChip-label': { px: 1.5 },
                                    ...(statusFilter === 'all' && {
                                        backgroundColor: theme.palette.primary.main,
                                        color: '#fff',
                                    }),
                                }}
                            />
                            <Chip
                                icon={<HourglassEmpty sx={{ fontSize: '0.9rem !important' }} />}
                                label={`Pending (${statusStats.pending})`}
                                size='small'
                                variant={statusFilter === OrderStatus.PENDING ? 'filled' : 'outlined'}
                                onClick={() => setStatusFilter(OrderStatus.PENDING)}
                                sx={{
                                    fontFamily: 'primaryFont',
                                    fontSize: '0.75rem',
                                    fontWeight: statusFilter === OrderStatus.PENDING ? 600 : 500,
                                    height: 32,
                                    px: 1.5,
                                    '& .MuiChip-label': { px: 1.25 },
                                    ...(statusFilter === OrderStatus.PENDING && {
                                        backgroundColor: getOrderStatusColor(OrderStatus.PENDING, theme),
                                        color: '#fff',
                                        '& .MuiChip-icon': { color: '#fff' },
                                    }),
                                }}
                            />
                            <Chip
                                icon={<Loop sx={{ fontSize: '0.9rem !important' }} />}
                                label={`Processing (${statusStats.processing})`}
                                size='small'
                                variant={statusFilter === OrderStatus.PROCESSING ? 'filled' : 'outlined'}
                                onClick={() => setStatusFilter(OrderStatus.PROCESSING)}
                                sx={{
                                    fontFamily: 'primaryFont',
                                    fontSize: '0.75rem',
                                    fontWeight: statusFilter === OrderStatus.PROCESSING ? 600 : 500,
                                    height: 32,
                                    px: 1.5,
                                    '& .MuiChip-label': { px: 1.25 },
                                    ...(statusFilter === OrderStatus.PROCESSING && {
                                        backgroundColor: getOrderStatusColor(OrderStatus.PROCESSING, theme),
                                        color: '#fff',
                                        '& .MuiChip-icon': { color: '#fff' },
                                    }),
                                }}
                            />
                            <Chip
                                icon={<LocalShippingOutlined sx={{ fontSize: '0.9rem !important' }} />}
                                label={`Shipped (${statusStats.shipped})`}
                                size='small'
                                variant={statusFilter === OrderStatus.SHIPPED ? 'filled' : 'outlined'}
                                onClick={() => setStatusFilter(OrderStatus.SHIPPED)}
                                sx={{
                                    fontFamily: 'primaryFont',
                                    fontSize: '0.75rem',
                                    fontWeight: statusFilter === OrderStatus.SHIPPED ? 600 : 500,
                                    height: 32,
                                    px: 1.5,
                                    '& .MuiChip-label': { px: 1.25 },
                                    ...(statusFilter === OrderStatus.SHIPPED && {
                                        backgroundColor: getOrderStatusColor(OrderStatus.SHIPPED, theme),
                                        color: '#fff',
                                        '& .MuiChip-icon': { color: '#fff' },
                                    }),
                                }}
                            />
                            <Chip
                                icon={<CheckCircle sx={{ fontSize: '0.9rem !important' }} />}
                                label={`Delivered (${statusStats.delivered})`}
                                size='small'
                                variant={statusFilter === OrderStatus.DELIVERED ? 'filled' : 'outlined'}
                                onClick={() => setStatusFilter(OrderStatus.DELIVERED)}
                                sx={{
                                    fontFamily: 'primaryFont',
                                    fontSize: '0.75rem',
                                    fontWeight: statusFilter === OrderStatus.DELIVERED ? 600 : 500,
                                    height: 32,
                                    px: 1.5,
                                    '& .MuiChip-label': { px: 1.25 },
                                    ...(statusFilter === OrderStatus.DELIVERED && {
                                        backgroundColor: getOrderStatusColor(OrderStatus.DELIVERED, theme),
                                        color: '#fff',
                                        '& .MuiChip-icon': { color: '#fff' },
                                    }),
                                }}
                            />
                        </Box>
                    </Box>
                </Box>
            )}

            {/* Controls Bar */}
            <TableControlBar
                dense={dense}
                onDenseChange={(newDense) => {
                    setDense(newDense);
                    updateOrdersPreferences({ dense: newDense });
                }}
                isLoading={isLoading}
                onRefresh={handleRefresh}
                activeFilter={deliveryFilter}
                onFilterChange={(value) => handleDeliveryFilterChange(value as 'all' | 'sameday' | 'standard')}
                filters={[
                    { label: 'All', value: 'all' },
                    { 
                        label: 'Same Day', 
                        value: 'sameday', 
                        count: deliveryStats.sameday,
                        icon: <FlashOn fontSize='small' />,
                        color: 'warning'
                    },
                    { 
                        label: 'Standard', 
                        value: 'standard', 
                        count: deliveryStats.standard,
                        icon: <LocalShipping fontSize='small' />,
                        color: 'info'
                    }
                ]}
                search={{
                    value: searchQuery,
                    onChange: setSearchQuery,
                    placeholder: 'Search by order ref, email...'
                }}
                activeFilterCount={
                    (deliveryFilter !== 'all' ? 1 : 0) +
                    (statusFilter !== 'all' ? 1 : 0) +
                    (searchQuery ? 1 : 0)
                }
            />

            <PortalTable>
                <TableContainer>
                    <Table stickyHeader size={dense ? 'small' : 'medium'}>
                        <TableHead>
                            <TableRow>
                                {/* Order Ref - always visible */}
                                <TableCell>
                                    <TableSortLabel
                                        sx={{ ml: { xs: 1, sm: 8 }, fontFamily: "primaryFont", fontWeight: "bold", letterSpacing: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                                        active={orderBy === 'reference'}
                                        direction={orderBy === 'reference' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'reference')}
                                    >
                                        Order
                                    </TableSortLabel>
                                </TableCell>

                                {/* Mobile: Combined Status header */}
                                <TableCell align="right" sx={{ display: { xs: 'table-cell', sm: 'none' } }}>
                                    <TableSortLabel
                                        sx={{ fontFamily: "primaryFont", fontWeight: "bold", letterSpacing: 1, fontSize: '0.75rem' }}
                                        active={orderBy === 'status'}
                                        direction={orderBy === 'status' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'status')}
                                    >
                                        Status
                                    </TableSortLabel>
                                </TableCell>

                                {/* Desktop columns */}
                                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                    <TableSortLabel
                                        sx={{ fontFamily: "primaryFont", fontWeight: "bold", letterSpacing: 1 }}
                                        active={orderBy === 'status'}
                                        direction={orderBy === 'status' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'status')}
                                    >
                                        Order Status
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                    <TableSortLabel
                                        sx={{ fontFamily: "primaryFont", fontWeight: "bold", letterSpacing: 1 }}
                                        active={orderBy === 'deliveryType'}
                                        direction={orderBy === 'deliveryType' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'deliveryType')}
                                    >
                                        Delivery Type
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                    <TableSortLabel
                                        sx={{ fontFamily: "primaryFont", fontWeight: "bold", letterSpacing: 1 }}
                                        active={orderBy === 'email'}
                                        direction={orderBy === 'email' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'email')}
                                    >
                                        Email
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right" sx={{ display: { xs: "none", sm: "table-cell" } }}>
                                    <TableSortLabel
                                        sx={{ fontFamily: "primaryFont", fontWeight: "bold", letterSpacing: 1 }}
                                        active={orderBy === 'totalCost'}
                                        direction={orderBy === 'totalCost' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'totalCost')}
                                    >
                                        Total Cost
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right" sx={{ display: { xs: "none", sm: "table-cell" } }}>
                                    <TableSortLabel
                                        sx={{ fontFamily: "primaryFont", fontWeight: "bold", letterSpacing: 1 }}
                                        active={orderBy === 'created'}
                                        direction={orderBy === 'created' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'created')}
                                    >
                                        Created
                                    </TableSortLabel>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedOrders?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((order) => {
                                return (
                                    <OrderRow
                                        key={order.id}
                                        order={order}
                                    />
                                );
                            })}
                            {emptyRows > 0 && (
                                <TableRow style={{ height: (dense ? 33 : 53) * emptyRows }}>
                                    <TableCell colSpan={6} />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={filteredOrders?.length || 0}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                        '& .MuiTablePagination-select': {
                            fontFamily: 'primaryFont'
                        },
                        '& .MuiTablePagination-displayedRows': {
                            fontFamily: 'primaryFont'
                        }
                    }}
                />
            </PortalTable>
        </Box>
    );
};